import React, { useState } from "react";
import { api } from "@/lib/api";
import { Trash2, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Chip, Stat, Loading, Empty, SectionHeader, LoadError, usePlatformFetch } from "./_primitives";

const tagClass = { test: "amber", demo: "p2", certification: "p3" };

export default function CleanlinessCenter() {
  const { data, error, retry: reload } = usePlatformFetch("/platform/cleanliness");
  const [busy, setBusy] = useState(null);

  const archive = async (tid, name) => {
    if (!window.confirm(`Archive ${name}? This sets status=archived and disables logins. Reversible via Customers tab.`)) return;
    setBusy(tid);
    try {
      await api.patch(`/platform/tenants/${tid}/status`, { status: "archived", reason: "production-cleanliness" });
      toast.success(`${name} archived`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally { setBusy(null); }
  };

  if (error) return <LoadError error={error} retry={reload} label="Cleanliness" />;
  if (!data) return <Loading />;
  const s = data.summary || {};

  return (
    <div className="space-y-4" data-testid="cleanliness-center">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Test tenants" value={s.test_tenants ?? 0} accent={s.test_tenants ? "amber" : undefined} />
        <Stat label="Demo tenants" value={s.demo_tenants ?? 0} />
        <Stat label="Certification tenants" value={s.certification_tenants ?? 0} />
        <Stat label="Orphan users" value={s.orphan_users ?? 0} accent={s.orphan_users ? "red" : undefined} />
        <Stat label="Expired invites" value={s.expired_invites ?? 0} />
        <Stat label="Broken shares" value={s.broken_shares ?? 0} accent={s.broken_shares ? "amber" : undefined} sub="revoked but recently used" />
        <Stat label="Empty old projects" value={s.empty_old_projects ?? 0} sub=">30d, no sheets" />
      </div>

      <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="cleanliness-tenants">
        <SectionHeader icon={<Sparkles size={11} />} title={`Test / demo / certification tenants (${(data.test_tenants || []).length})`} />
        {(data.test_tenants || []).length === 0 ? <Empty>Production is clean — no test/demo/cert tenants identified.</Empty> : (
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Tenant</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Tag</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Status</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Created</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-mono">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.test_tenants || []).map((t) => (
                <tr key={t.tenant_id} className="border-t border-[var(--fop-border)]" data-testid={`clean-row-${t.tenant_id}`}>
                  <td className="px-3 py-2"><span className="font-display text-sm">{t.tenant_name}</span><div className="text-[10px] font-mono text-[var(--fop-text-4)]">{t.slug}</div></td>
                  <td className="px-3 py-2"><Chip state={tagClass[t.tag] || "amber"} label={t.tag} /></td>
                  <td className="px-3 py-2"><Chip state={t.status === "active" ? "ok" : "missing"} label={t.status} /></td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[var(--fop-text-4)]">{(t.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right">
                    {t.status === "active" ? (
                      <button onClick={() => archive(t.tenant_id, t.tenant_name)} disabled={busy === t.tenant_id} data-testid={`clean-archive-${t.tenant_id}`} className="btn btn-ghost text-[10px] inline-flex items-center gap-1.5 disabled:opacity-50">
                        <Trash2 size={11} /> Archive
                      </button>
                    ) : <span className="text-[10px] font-mono text-[var(--fop-text-4)]">archived</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data.orphan_users || []).length > 0 && (
        <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="cleanliness-orphans">
          <SectionHeader icon={<AlertCircle size={11} />} title={`Orphan users (${data.orphan_users.length})`} />
          <table className="w-full text-xs">
            <tbody>
              {data.orphan_users.map((u) => (
                <tr key={u.id} className="border-t border-[var(--fop-border)]" data-testid={`orphan-row-${u.id}`}>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{u.email}</td>
                  <td className="px-3 py-1.5 text-[10px] text-[var(--fop-text-4)]">tenant_id: {u.tenant_id}</td>
                  <td className="px-3 py-1.5 text-[10px] text-[var(--fop-text-4)]">{u.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
